import { useContext, useMemo } from "react";
import {
  useMediaQuery,
  WhmAccordion,
  WhmAccordionDetails,
  WhmAccordionSummary,
  WhmAvatar,
  WhmDivider,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CurrencyFormat } from "@securitize/reactjs-sid-utils";
import { Translate } from "../../../../../Translation/Translate";
import {
  I18nContext,
  translationKeys,
} from "../../../../../../contexts/I18nContext";
import { TranslationKey } from "../../../../../../contexts/I18nContext/translationKeys";
import {
  FundBalanceDto,
  FundInvestorBalancesDto,
} from "../../../../../../data/dtos/get-fund-balances.dto";
import { formatDateOnlyString } from "../../../../../../utils/dateUtils";

export const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

function InvestorStat({
  labelKey,
  children,
}: {
  labelKey: TranslationKey;
  children: React.ReactNode;
}) {
  return (
    <WhmStack sx={{ flex: 1 }}>
      <WhmTypography variant="body2" color="text.secondary">
        <Translate translationKey={labelKey} />
      </WhmTypography>
      <WhmTypography>{children}</WhmTypography>
    </WhmStack>
  );
}

function FundRow({ fundName, balance, currency, asOfDate }: FundBalanceDto) {
  const { language } = useContext(I18nContext);
  const balanceFormat = useMemo(
    () => new CurrencyFormat({ currency }),
    [currency],
  );

  return (
    <WhmStack
      direction="row"
      gap="16px"
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      <WhmStack>
        <WhmTypography>{fundName}</WhmTypography>
        <WhmTypography variant="body2" color="text.secondary">
          <Translate
            translationKey={translationKeys.PORTFOLIO_SFS_AS_OF}
            replacements={{ date: formatDateOnlyString(asOfDate, language) }}
          />
        </WhmTypography>
      </WhmStack>
      {/* balance is a decimal string: CurrencyFormat formats it without a float round-trip */}
      <WhmTypography sx={{ whiteSpace: "nowrap" }}>
        {balanceFormat.format(balance)}
      </WhmTypography>
    </WhmStack>
  );
}

const usdFormat = new CurrencyFormat({ currency: "USD" });

export default function SfsInvestorCard({
  investorName,
  usdTotalBalance,
  balances,
}: FundInvestorBalancesDto) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  return (
    <WhmAccordion
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        "&::before": { display: "none" },
      }}
    >
      <WhmAccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ color: "action.active" }} />}
        sx={{
          px: 4,
          "& .MuiAccordionSummary-content": { minWidth: 0 },
        }}
      >
        <WhmStack
          direction="row"
          gap="16px"
          sx={{ alignItems: "center", flex: 1, minWidth: 0 }}
        >
          <WhmStack
            direction="row"
            gap="16px"
            sx={{ alignItems: "center", flex: 2, minWidth: 0 }}
          >
            <WhmAvatar
              sx={{
                width: 32,
                height: 32,
                fontSize: 12,
                color: "primary.main",
                backgroundColor: "primary.light",
              }}
            >
              {getInitials(investorName)}
            </WhmAvatar>
            <WhmStack sx={{ minWidth: 0 }}>
              <WhmTypography fontWeight={500} noWrap>
                {investorName}
              </WhmTypography>
              {isMobile && (
                <WhmTypography variant="body2" color="text.secondary">
                  <Translate
                    translationKey={
                      balances.length === 1
                        ? translationKeys.PORTFOLIO_SFS_FUNDS_COUNT_ONE
                        : translationKeys.PORTFOLIO_SFS_FUNDS_COUNT_OTHER
                    }
                    replacements={{ count: balances.length }}
                  />
                </WhmTypography>
              )}
            </WhmStack>
          </WhmStack>
          {isMobile ? (
            <WhmTypography sx={{ whiteSpace: "nowrap" }}>
              {usdFormat.format(usdTotalBalance)}
            </WhmTypography>
          ) : (
            <>
              <InvestorStat labelKey={translationKeys.PORTFOLIO_SFS_FUNDS}>
                {balances.length}
              </InvestorStat>
              <InvestorStat
                labelKey={translationKeys.PORTFOLIO_SFS_TOTAL_VALUE}
              >
                {usdFormat.format(usdTotalBalance)}
              </InvestorStat>
            </>
          )}
        </WhmStack>
      </WhmAccordionSummary>
      <WhmDivider />
      <WhmAccordionDetails sx={{ px: 4, py: 3 }}>
        <WhmStack gap="24px">
          {balances.map((balance, index) => (
            <FundRow key={`${balance.fundName}-${index}`} {...balance} />
          ))}
        </WhmStack>
      </WhmAccordionDetails>
    </WhmAccordion>
  );
}
